import { useCallback, useMemo } from "react";
import { MiniMapHeader } from "./MiniMapHeader";
import { DragPanTooltip } from "./DragPanTooltip";
import { MiniMapZoomControls } from "./MiniMapZoomControls";
import { MiniMapBackground } from "./MiniMapBackground";
import { DebugCameraPositions } from "./DebugCameraPositions";
import { NorthArrow } from "./NorthArrow";
import { PathLine } from "./PathLine";
import { PathMarkers } from "./PathMarkers";
import { useSvgImageLoad } from "hooks/useSvgMapControls/useSvgImageLoad";
import { useMiniMapControls } from "./hooks/useMiniMapControls";
import {
  MINIMAP_CURSORS,
  MINIMAP_INTERACTION_MODES,
} from "./hooks/useMiniMapControls/constants";
import { buildMiniMapPlanPoints } from "./utils";
import { reversePlanAlignmentFromPoint } from "utils/math/reversePlanAlignmentFromPoint";
import { calculateTranslationFromOpticalCenter } from "utils/math/calculateTranslationFromOpticalCenter";
import { BASE_VIEWBOX } from "./constants";
import {
  MiniMapProps,
  OnSelectedFrameIndexChange,
  HandleChangeFrameState,
} from "./types";

export const MiniMap = ({
  showAdminTools,
  onEditAlignment,
  onEditFrameState,
  uploadPreviewImage,
  setEditedFrameFields,
  isEditingAlignment,
  isEditingFrameState,
  isUploadingPreviewImage,
  minimapSize,
  setMinimapSize,
  floorPlanAlignment,
  cameraFrames,
  currentFrameIndex,
  planPreviewUrl,
  debugCameraPositions,
  changeSelectedFrameIndex,
}: MiniMapProps) => {
  const isLargeMiniMap = minimapSize === "large";

  const {
    isImageLoaded: isMinimapImageLoaded,
    onImageLoad: onMinimapImageLoad,
    onImageError: onMinimapImageError,
  } = useSvgImageLoad({ src: planPreviewUrl });

  const { planPoints, pathLinePoints, arrowPoints } = useMemo(
    () =>
      buildMiniMapPlanPoints({
        cameraFrames,
        floorPlanAlignment,
      }),
    [cameraFrames, floorPlanAlignment],
  );

  const {
    svgRef,
    isSpacePressed,
    svgPointFromMouse,
    cursor: miniMapControlsCursor,
    onMouseDown,
    onMouseMove,
    onMouseUp,
    onMouseLeave,
    zoomIn,
    zoomOut,
    resetZoom,
  } = useMiniMapControls({
    baseViewBox: BASE_VIEWBOX,
    isDisabled: !isLargeMiniMap,
    interactionMode: showAdminTools
      ? MINIMAP_INTERACTION_MODES.EDIT
      : MINIMAP_INTERACTION_MODES.VIEW,
  });

  const onSelectedFrameIndexChange: OnSelectedFrameIndexChange = useCallback(
    ({ newIndex, frameName }) => {
      if (isSpacePressed || isEditingFrameState) return; // Prevent clicks while panning
      console.log(
        `Mini-map: Clicking on START frame ${newIndex}: ${frameName}`,
      );
      changeSelectedFrameIndex({ newIndex, method: "mini-map-click" });
    },
    [isSpacePressed, isEditingFrameState, changeSelectedFrameIndex],
  );

  const handleMiniMapResize = useCallback(() => {
    if (isLargeMiniMap) {
      setMinimapSize("normal");
      resetZoom();
      return;
    }

    setMinimapSize("large");
  }, [isLargeMiniMap, setMinimapSize, resetZoom]);

  const handleChangeFrameState: HandleChangeFrameState = useCallback(
    (e) => {
      if (isSpacePressed || !isEditingFrameState) return;
      const svgPoint = svgPointFromMouse(e);

      if (!svgPoint) return;

      const opticalCenter = reversePlanAlignmentFromPoint({
        point: svgPoint,
        planAlignment: floorPlanAlignment,
      });

      const translation = calculateTranslationFromOpticalCenter({
        opticalCenter: [
          opticalCenter.x,
          opticalCenter.y,
          cameraFrames[currentFrameIndex].processedPosition[2],
        ],
        rotation: cameraFrames[currentFrameIndex].rotation,
      });

      setEditedFrameFields({
        processedPosition: [
          opticalCenter.x,
          opticalCenter.y,
          cameraFrames[currentFrameIndex].processedPosition[2],
        ],
        position: translation,
      });
    },
    [
      cameraFrames,
      currentFrameIndex,
      floorPlanAlignment,
      isEditingFrameState,
      isSpacePressed,
      setEditedFrameFields,
      svgPointFromMouse,
    ],
  );

  const onUpdatePreviewImage = useCallback(async () => {
    if (svgRef.current) {
      uploadPreviewImage(svgRef.current);
    }
  }, [svgRef, uploadPreviewImage]);

  return (
    cameraFrames.length > 0 && (
      <div
        className={`absolute top-4 right-4 bg-white overflow-hidden rounded-lg shadow-lg transition-all duration-300 z-30 ${
          isLargeMiniMap ? "w-[500px]" : "w-80"
        }`}
      >
        <MiniMapHeader
          minimapSize={minimapSize}
          showAdminTools={showAdminTools}
          setMinimapSize={handleMiniMapResize}
          onEditAlignment={onEditAlignment}
          onEditFrameState={onEditFrameState}
          onUpdatePreviewImage={onUpdatePreviewImage}
          isEditingAlignment={isEditingAlignment}
          isEditingFrameState={isEditingFrameState}
          isUploadingPreviewImage={isUploadingPreviewImage}
          isMinimapImageLoaded={isMinimapImageLoaded}
        />
        {isLargeMiniMap && (
          <>
            {showAdminTools && !isSpacePressed && <DragPanTooltip />}
            <MiniMapZoomControls
              handleZoomIn={zoomIn}
              handleZoomOut={zoomOut}
              handleResetZoom={resetZoom}
            />
          </>
        )}
        <svg
          ref={svgRef}
          onClick={handleChangeFrameState}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseLeave}
          className={`group${isEditingFrameState ? " edit-mode" : ""}`}
          style={{
            cursor:
              miniMapControlsCursor === MINIMAP_CURSORS.DEFAULT &&
              isEditingFrameState
                ? MINIMAP_CURSORS.CROSSHAIR
                : miniMapControlsCursor,
            touchAction: "none",
            overscrollBehavior: "contain",
          }}
        >
          <MiniMapBackground
            width={BASE_VIEWBOX.w}
            height={BASE_VIEWBOX.h}
            imageUrl={planPreviewUrl}
            onImageLoad={onMinimapImageLoad}
            onImageError={onMinimapImageError}
          />
          <PathLine pathLinePoints={pathLinePoints} arrowPoints={arrowPoints} />
          <PathMarkers
            planPoints={planPoints}
            currentFrameIndex={currentFrameIndex}
            onSelectedFrameIndexChange={onSelectedFrameIndexChange}
          />
          {debugCameraPositions && (
            <DebugCameraPositions
              startPosition={{
                x: planPoints[0].x,
                y: planPoints[0].y,
                frameName: planPoints[0].frameName,
              }}
              floorPlanAlignment={floorPlanAlignment}
            />
          )}
          <NorthArrow />
        </svg>
      </div>
    )
  );
};
